import { CheckCircle2, Pencil, Plus, RefreshCw } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { toast } from 'sonner';
import { useActionsPaie, useRemunerationAgent } from '@/api/hooks';
import { type RubriqueRemunerationSaisie } from '@/api/types';
import { Chargement, ErreurBloc, Vide } from '@/components/common';
import { StatutRemunerationBadge } from '@/components/paie';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SelectNatif } from '@/components/ui/select-natif';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { dateFr, dateHeureFr, montant, nombre } from '@/lib/utils';

const erreur = (e: unknown, defaut: string) => toast.error(e instanceof Error ? e.message : defaut);
const Champ = ({ label, children }: { label: string; children: ReactNode }) => (
  <div>
    <span className="text-muted-foreground">{label}</span>
    <div>{children ?? '—'}</div>
  </div>
);
const usd = (n: number | null | undefined) => montant(n, true);

/**
 * Fiche individuelle de rémunération de référence (spécifications HRMS et Paie, § 5.3 à 5.5 ; lot HP-2) : barème appliqué,
 * annuités, rubriques, assiettes et net indicatif ; situation d'ouverture, contrôle RH, approbation DRH, visa DF.
 */
export function RemunerationAgent({ agentId, moi = false }: { agentId: string; moi?: boolean }) {
  const { data: r, isLoading, error } = useRemunerationAgent(agentId);
  const actions = useActionsPaie();
  const [dialogue, setDialogue] = useState<null | 'situation' | 'rubrique'>(null);
  if (isLoading) return <Chargement />;
  if (error || !r) return <ErreurBloc erreur={error} />;
  const c = r.calcul;
  const agir = async (promesse: Promise<unknown>, succes: string, defaut: string) => {
    try {
      await promesse;
      toast.success(succes);
    } catch (e) {
      erreur(e, defaut);
    }
  };
  return (
    <div className="space-y-6" data-testid="remuneration-agent">
      <Card>
        <CardHeader>
          <CardTitle className="flex flex-wrap items-center justify-between gap-2">
            <span className="flex items-center gap-2">
              Rémunération de référence <StatutRemunerationBadge statut={r.fiche.statut} />
              {r.fiche.cadreDirection && <Badge variant="outline">cadre de direction</Badge>}
              {r.fiche.classe && <Badge variant="outline">classe {r.fiche.classe}</Badge>}
            </span>
            <span className="flex flex-wrap gap-2">
              {r.droits.gerer && (
                <Button size="sm" variant="outline" onClick={() => agir(actions.recalculer.mutateAsync(agentId), 'Fiche recalculée.', 'Recalcul impossible.')} disabled={actions.recalculer.isPending}>
                  <RefreshCw className="size-4" /> Recalculer
                </Button>
              )}
              {r.droits.controler && (
                <Button size="sm" onClick={() => agir(actions.controler.mutateAsync({ id: agentId }), 'Fiche contrôlée : le Directeur RH est notifié.', 'Contrôle impossible.')} disabled={actions.controler.isPending} data-testid="controler-fiche">
                  <CheckCircle2 className="size-4" /> Contrôler
                </Button>
              )}
              {r.droits.approuver && (
                <Button size="sm" onClick={() => agir(actions.approuver.mutateAsync({ id: agentId }), 'Fiche approuvée : le Directeur financier est notifié.', 'Approbation impossible.')} disabled={actions.approuver.isPending} data-testid="approuver-fiche">
                  <CheckCircle2 className="size-4" /> Approuver (DRH)
                </Button>
              )}
              {r.droits.viser && (
                <Button size="sm" onClick={() => agir(actions.viser.mutateAsync({ id: agentId }), 'Fiche visée : rémunération de référence approuvée.', 'Visa impossible.')} disabled={actions.viser.isPending} data-testid="viser-fiche">
                  <CheckCircle2 className="size-4" /> Viser (DF)
                </Button>
              )}
            </span>
          </CardTitle>
          <CardDescription>
            {r.fiche.horsBareme ? 'Mandataire ou membre du Conseil : hors barème (D7).' : r.bareme ? `Barème version ${r.bareme.numero} (${r.bareme.libelle}, effet du ${dateFr(r.bareme.debut)}) · ligne ${r.bareme.ligne ? `« ${r.bareme.ligne.grade} »` : 'à déterminer'}` : 'Aucune version du barème en vigueur.'}
            {r.fiche.motifRevalidation ? ` · à régulariser : ${r.fiche.motifRevalidation}` : ''}
            {r.fiche.controle ? ` · contrôlée par ${r.fiche.controle.par ?? '—'} le ${dateHeureFr(r.fiche.controle.le)}` : ''}
            {r.fiche.approbationDrh ? ` · approuvée par ${r.fiche.approbationDrh.par ?? '—'} le ${dateHeureFr(r.fiche.approbationDrh.le)}` : ''}
            {r.fiche.visaDf ? ` · visée par ${r.fiche.visaDf.par ?? '—'} le ${dateHeureFr(r.fiche.visaDf.le)}` : ''}
            {moi ? ' · Hors présences, variables et retenues : la paie mensuelle part de cette référence.' : ''}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {r.fiche.anomalies.length > 0 && (
            <ul className="space-y-1 text-sm" data-testid="anomalies-remuneration">
              {r.fiche.anomalies.map((a) => (
                <li key={a.code} className={a.message.endsWith('(bloquante)') ? 'text-destructive' : 'text-amber-800'}>
                  {a.message.replace(' (bloquante)', '')}
                  {a.message.endsWith('(bloquante)') && <Badge variant="destructive" className="ml-2 text-[10px]">bloquante</Badge>}
                </li>
              ))}
            </ul>
          )}
          {r.droits.voirMontants && !r.fiche.horsBareme && (
            <div className="grid gap-3 text-sm sm:grid-cols-2 lg:grid-cols-4">
              <Champ label="Brut du barème">{usd(c.brutBareme)}</Champ>
              <Champ label="Annuités cumulées">{usd(c.annuitesCumulees)}</Champ>
              <Champ label="Brut de référence">
                <span className="font-medium">{usd(c.brutReference)}</span>
              </Champ>
              <Champ label="Prime du grade">
                {usd(c.prime)}
                {c.prime > 0 ? <span className="text-xs text-muted-foreground"> · {c.primeImposable ? 'imposable et cotisable' : 'exonérée'}</span> : null}
              </Champ>
              <Champ label={`Assiette sociale · CNSS ${nombre(c.tauxCnss, 2)} %`}>
                {usd(c.assietteSociale)} · <span className="text-destructive">− {usd(c.cnss)}</span>
              </Champ>
              <Champ label="Rémunération imposable · net imposable">
                {usd(c.remunerationImposable)} · {usd(c.netImposable)}
              </Champ>
              <Champ label="Logement · transport">
                {usd(c.logement)} · {usd(c.transport)}
              </Champ>
              <Champ label="Impôt indicatif">
                {c.impot ? (
                  <>
                    <span className="text-destructive">− {usd(c.impot.montantUsd)}</span>
                    <div className="text-xs text-muted-foreground">
                      base {nombre(c.impot.baseCdf, 0)} CDF au taux de {nombre(c.impot.tauxChange, 0)} · {c.impot.plafonne ? 'plafonné' : c.impot.tranches.map((t) => `${t.taux} %`).join(', ')}
                    </div>
                  </>
                ) : (
                  <span className="text-muted-foreground">non calculé (barème d’impôt ou taux de change absent)</span>
                )}
              </Champ>
              <Champ label="Net indicatif (hors présences et retenues)">
                <span className="font-semibold">{c.netIndicatif === null ? '—' : usd(c.netIndicatif)}</span>
              </Champ>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid min-w-0 gap-6 lg:grid-cols-2">
        <Card className="min-w-0" data-testid="anciennete-remuneration">
          <CardHeader>
            <CardTitle className="flex flex-wrap items-center justify-between gap-2">
              Ancienneté et annuités
              <span className="flex gap-2">
                {r.droits.gerer && (
                  <Button size="sm" variant="outline" onClick={() => setDialogue('situation')}>
                    <Pencil className="size-4" /> Situation d’ouverture
                  </Button>
                )}
                {r.droits.validerSituation && (
                  <Button size="sm" onClick={() => agir(actions.validerSituation.mutateAsync(agentId), 'Situation d’ouverture validée : annuités calculées.', 'Validation impossible.')} disabled={actions.validerSituation.isPending} data-testid="valider-situation">
                    <CheckCircle2 className="size-4" /> Valider (DRH)
                  </Button>
                )}
              </span>
            </CardTitle>
            <CardDescription>
              Référence {r.anciennete.reference ? dateFr(r.anciennete.reference) : '—'} · {r.anciennete.annees === null ? '—' : `${r.anciennete.annees} an(s) et ${r.anciennete.mois} mois`} · prochain anniversaire {r.anciennete.prochainAnniversaire ? dateFr(r.anciennete.prochainAnniversaire) : '—'}
              {r.anciennete.tauxApplicable !== null ? ` · taux applicable ${nombre(r.anciennete.tauxApplicable, 0)} %${r.fiche.cadreDirection ? ' (cadre de direction)' : r.fiche.classe ? ` (classe ${r.fiche.classe})` : ''}` : ''}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            {r.situationOuverture ? (
              <div className="rounded-md border p-3">
                <div className="font-medium">Situation d’ouverture {r.situationOuverture.valideLe ? <Badge variant="outline" className="ml-1 border-emerald-300 bg-emerald-50 text-emerald-800">validée</Badge> : <Badge variant="secondary" className="ml-1">à valider par le DRH</Badge>}</div>
                <div className="text-muted-foreground">
                  Traitement reconnu {r.droits.voirMontants ? usd(r.situationOuverture.traitementReconnu) : '•••'} · dernière revalorisation le {dateFr(r.situationOuverture.dateDerniereRevalorisation)} · {r.situationOuverture.annuitesIntegrees} annuité(s) intégrée(s)
                  {r.situationOuverture.commentaire ? ` · ${r.situationOuverture.commentaire}` : ''}
                  <div>
                    saisie par {r.situationOuverture.saisiPar ?? '—'} le {dateFr(r.situationOuverture.saisiLe)}
                    {r.situationOuverture.valideLe ? ` · validée par ${r.situationOuverture.validePar ?? '—'} le ${dateFr(r.situationOuverture.valideLe)}` : ''}
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-muted-foreground">Aucune situation d’ouverture : les annuités ne sont calculées qu’après sa validation (D15).</p>
            )}
            {!r.annuites.length ? (
              <Vide message="Aucune annuité calculée." />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Anniversaire</TableHead>
                    <TableHead>Années</TableHead>
                    <TableHead>Taux</TableHead>
                    {r.droits.voirMontants && <TableHead>Assiette → augmentation</TableHead>}
                    <TableHead>Statut</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {r.annuites.map((a) => (
                    <TableRow key={a.id}>
                      <TableCell>{dateFr(a.anniversaire)}</TableCell>
                      <TableCell>{a.anneesRevolues}</TableCell>
                      <TableCell>
                        {nombre(a.taux, 0)} %{a.cadreDirection ? ' · cadre' : a.classe ? ` · classe ${a.classe}` : ''}
                      </TableCell>
                      {r.droits.voirMontants && (
                        <TableCell>
                          {usd(a.assietteAvant)} → +{usd(a.augmentation)} = {usd(a.assietteApres)}
                        </TableCell>
                      )}
                      <TableCell>
                        <Badge variant={a.statut === 'APPLIQUEE' ? 'default' : a.statut === 'ANNULEE' ? 'outline' : 'secondary'}>{a.statut === 'APPLIQUEE' ? 'Appliquée' : a.statut === 'ANNULEE' ? 'Annulée' : 'Calculée'}</Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <Card className="min-w-0" data-testid="rubriques-remuneration">
          <CardHeader>
            <CardTitle className="flex flex-wrap items-center justify-between gap-2">
              Rubriques
              {r.droits.gerer && (
                <Button size="sm" variant="outline" onClick={() => setDialogue('rubrique')}>
                  <Plus className="size-4" /> Rubrique individuelle
                </Button>
              )}
            </CardTitle>
            <CardDescription>Rubriques du grade (dispositions particulières des cadres de direction) et dispositions individuelles approuvées (décision, pièce) ; traitement fiscal et social rubrique par rubrique.</CardDescription>
          </CardHeader>
          <CardContent>
            {!c.rubriques.length ? (
              <Vide message="Aucune rubrique en dehors du barème." />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Rubrique</TableHead>
                    {r.droits.voirMontants && <TableHead>Montant</TableHead>}
                    <TableHead>Traitement</TableHead>
                    <TableHead>Origine</TableHead>
                    {r.droits.gerer && <TableHead />}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {c.rubriques.map((x) => (
                    <TableRow key={x.id}>
                      <TableCell>
                        {x.libelle}
                        <div className="text-xs text-muted-foreground">
                          {x.code}
                          {x.debut ? ` · du ${dateFr(x.debut)}` : ''}
                          {x.fin ? ` au ${dateFr(x.fin)}` : ''}
                          {x.decision ? ` · ${x.decision}` : ''}
                        </div>
                      </TableCell>
                      {r.droits.voirMontants && (
                        <TableCell>
                          {x.devise === 'USD' ? usd(x.montant) : `${nombre(x.montant, 2)} CDF`}
                        </TableCell>
                      )}
                      <TableCell className="text-xs">
                        {x.nature ? 'avantage en nature' : [x.imposable ? 'imposable' : 'non imposable', x.cotisable ? 'cotisable' : 'non cotisable'].join(', ')}
                      </TableCell>
                      <TableCell>{x.origine === 'GRADE' ? 'Grade' : 'Individuelle'}</TableCell>
                      {r.droits.gerer && (
                        <TableCell className="text-right">
                          {x.origine === 'INDIVIDUELLE' && (
                            <Button size="sm" variant="ghost" onClick={() => agir(actions.cloturerRubrique.mutateAsync({ id: agentId, rubriqueId: x.id }), 'Rubrique clôturée.', 'Clôture impossible.')}>
                              Clôturer
                            </Button>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {r.historique.length > 1 && (
        <Card>
          <CardHeader>
            <CardTitle>Historique de la fiche</CardTitle>
            <CardDescription>Une période par ligne du barème ou brut appliqué ; les annuités, la situation d’ouverture et les rubriques suivent la fiche.</CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Période</TableHead>
                  <TableHead>Grade appliqué</TableHead>
                  {r.droits.voirMontants && <TableHead>Brut du barème · annuités</TableHead>}
                  <TableHead>Statut</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {r.historique.map((h) => (
                  <TableRow key={h.id} className={h.effetAu === null ? 'font-medium' : 'text-muted-foreground'}>
                    <TableCell>
                      {dateFr(h.effetDu)} → {h.effetAu ? dateFr(h.effetAu) : 'en cours'}
                    </TableCell>
                    <TableCell>{h.gradeApplique ?? '—'}</TableCell>
                    {r.droits.voirMontants && (
                      <TableCell>
                        {usd(h.brutBareme)} · {usd(h.annuitesCumulees)}
                      </TableCell>
                    )}
                    <TableCell>
                      <StatutRemunerationBadge statut={h.statut} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {dialogue === 'situation' && <SituationDialog agentId={agentId} brutBareme={c.brutBareme} initiale={r.situationOuverture} onClose={() => setDialogue(null)} />}
      {dialogue === 'rubrique' && <RubriqueDialog agentId={agentId} onClose={() => setDialogue(null)} />}
    </div>
  );
}

function SituationDialog({ agentId, brutBareme, initiale, onClose }: { agentId: string; brutBareme: number; initiale: { traitementReconnu: number; dateDerniereRevalorisation: string; annuitesIntegrees: number; commentaire: string | null } | null; onClose: () => void }) {
  const actions = useActionsPaie();
  const [s, setS] = useState({ traitementReconnu: String(initiale?.traitementReconnu ?? brutBareme), dateDerniereRevalorisation: initiale?.dateDerniereRevalorisation ?? '', annuitesIntegrees: String(initiale?.annuitesIntegrees ?? 0), commentaire: initiale?.commentaire ?? '' });
  const valider = async () => {
    try {
      await actions.situationOuverture.mutateAsync({ id: agentId, corps: { traitementReconnu: Number(s.traitementReconnu), dateDerniereRevalorisation: s.dateDerniereRevalorisation, annuitesIntegrees: Number(s.annuitesIntegrees) || 0, commentaire: s.commentaire || null } });
      toast.success('Situation d’ouverture enregistrée : validation du Directeur RH attendue.');
      onClose();
    } catch (e) {
      erreur(e, 'Enregistrement impossible.');
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="dialogue-situation-ouverture">
        <DialogHeader>
          <DialogTitle>Situation d’ouverture des annuités</DialogTitle>
          <DialogDescription>Traitement brut reconnu à la reprise (annuités déjà intégrées), date de la dernière revalorisation pour ancienneté ; les annuités suivantes sont calculées par l’application après validation du Directeur RH (D15).</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1"><Label htmlFor="so-traitement">Traitement reconnu (USD) *</Label><Input id="so-traitement" type="number" step="0.01" min="0" value={s.traitementReconnu} onChange={(e) => setS({ ...s, traitementReconnu: e.target.value })} /></div>
          <div className="space-y-1"><Label htmlFor="so-date">Dernière revalorisation *</Label><Input id="so-date" type="date" value={s.dateDerniereRevalorisation} onChange={(e) => setS({ ...s, dateDerniereRevalorisation: e.target.value })} /></div>
          <div className="space-y-1"><Label htmlFor="so-annuites">Annuités déjà intégrées</Label><Input id="so-annuites" type="number" min="0" max="60" value={s.annuitesIntegrees} onChange={(e) => setS({ ...s, annuitesIntegrees: e.target.value })} /></div>
          <div className="space-y-1 sm:col-span-2"><Label htmlFor="so-com">Commentaire</Label><Textarea id="so-com" value={s.commentaire} onChange={(e) => setS({ ...s, commentaire: e.target.value })} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={valider} disabled={!s.dateDerniereRevalorisation || actions.situationOuverture.isPending}>Enregistrer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RubriqueDialog({ agentId, onClose }: { agentId: string; onClose: () => void }) {
  const actions = useActionsPaie();
  const [s, setS] = useState<Omit<RubriqueRemunerationSaisie, 'montant'> & { montant: string }>({ code: '', libelle: '', montant: '', devise: 'USD', imposable: true, cotisable: true, nature: false, debut: '', fin: '', decision: '', pieceId: '' });
  const valider = async () => {
    try {
      await actions.ajouterRubrique.mutateAsync({ id: agentId, corps: { code: s.code.trim().toUpperCase().replace(/[^A-Z0-9_]/g, '_'), libelle: s.libelle, montant: Number(s.montant), devise: s.devise, imposable: s.imposable, cotisable: s.cotisable, nature: s.nature, debut: s.debut || null, fin: s.fin || null, decision: s.decision || null, pieceId: s.pieceId || null } });
      toast.success('Rubrique ajoutée : fiche à régulariser.');
      onClose();
    } catch (e) {
      erreur(e, 'Ajout impossible.');
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl" data-testid="dialogue-rubrique">
        <DialogHeader>
          <DialogTitle>Rubrique individuelle</DialogTitle>
          <DialogDescription>Disposition individuelle approuvée (décision, pièce jointe au dossier) : traitement fiscal et social défini rubrique par rubrique (§ 7.2).</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1"><Label htmlFor="ru-code">Code *</Label><Input id="ru-code" value={s.code} placeholder="PRIME_RESPONSABILITE" onChange={(e) => setS({ ...s, code: e.target.value })} /></div>
          <div className="space-y-1"><Label htmlFor="ru-libelle">Libellé *</Label><Input id="ru-libelle" value={s.libelle} onChange={(e) => setS({ ...s, libelle: e.target.value })} /></div>
          <div className="space-y-1"><Label htmlFor="ru-montant">Montant *</Label><Input id="ru-montant" type="number" step="0.01" min="0" value={s.montant} onChange={(e) => setS({ ...s, montant: e.target.value })} /></div>
          <div className="space-y-1"><Label htmlFor="ru-devise">Devise</Label><SelectNatif id="ru-devise" value={s.devise} onChange={(e) => setS({ ...s, devise: e.target.value as 'USD' | 'CDF' })}><option value="USD">USD</option><option value="CDF">CDF</option></SelectNatif></div>
          <div className="space-y-1"><Label htmlFor="ru-debut">Début</Label><Input id="ru-debut" type="date" value={s.debut ?? ''} onChange={(e) => setS({ ...s, debut: e.target.value })} /></div>
          <div className="space-y-1"><Label htmlFor="ru-fin">Fin</Label><Input id="ru-fin" type="date" value={s.fin ?? ''} onChange={(e) => setS({ ...s, fin: e.target.value })} /></div>
          <div className="space-y-1 sm:col-span-2"><Label htmlFor="ru-decision">Décision (référence, date)</Label><Input id="ru-decision" value={s.decision ?? ''} onChange={(e) => setS({ ...s, decision: e.target.value })} /></div>
          <div className="space-y-1 sm:col-span-2"><Label htmlFor="ru-piece">Identifiant de la pièce jointe (dossier de l’agent)</Label><Input id="ru-piece" value={s.pieceId ?? ''} onChange={(e) => setS({ ...s, pieceId: e.target.value })} /></div>
          <div className="flex flex-col gap-2 sm:col-span-2">
            <label className="flex items-center gap-2 text-sm"><Checkbox checked={s.imposable} onCheckedChange={(v) => setS({ ...s, imposable: v === true })} /> Imposable</label>
            <label className="flex items-center gap-2 text-sm"><Checkbox checked={s.cotisable} onCheckedChange={(v) => setS({ ...s, cotisable: v === true })} /> Cotisable (CNSS)</label>
            <label className="flex items-center gap-2 text-sm"><Checkbox checked={Boolean(s.nature)} onCheckedChange={(v) => setS({ ...s, nature: v === true })} /> Avantage en nature (présenté, non versé)</label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={valider} disabled={!s.code || !s.libelle || s.montant === '' || actions.ajouterRubrique.isPending}>Ajouter</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
